// SPA-side meeting_events writer. Mirrors backend/app/events.py for the
// user-event leg: actions originated by the user (title change, speaker
// override, text correction, span attribution add/remove, paragraph
// split/rejoin, undo, resubmit_initiated) go DIRECTLY to Supabase via
// supabase-js, gated by the meeting_events INSERT policy which requires
// actor_type = 'user' AND actor_id = auth.uid().
//
// System events (Soniox phases, Claude detectors, lock/unlock-via-future-
// backend-side-effects, export-endpoint side-effects) are written by the
// backend via the service key (RLS bypass) -- they do NOT route through
// here.
//
// Failure policy mirrors the helper-level discipline from
// backend/app/events.py: this helper RAISES on Supabase errors. Callers
// decide best-effort (wrap + warn + continue) vs. blocking (treat the
// audit row as a precondition for the action -- the only Slice 1 case is
// resubmit_initiated's snapshot, which is the safety mechanism). See
// DECISIONS.md 2026-05-20 "Irreversibility safety pattern" + Slice 1
// design Q-B answer.

import { supabase } from "./supabase";

// Typed payload shapes for known event_types. The union allows callers
// to discriminate on event_type and get narrowed payload fields. The
// catch-all index signature keeps the open-jsonb contract intact so
// unknown / future event types compile without casts.
export interface EditSessionPayload {
  session_id: string;
  started_at: string;
  ended_at: string;
  turn_start_ms: number;
  sub_event_ids: string[];
  [k: string]: unknown;
}

// Multi-bookmark model (migration 0022): meetings.bookmarks_at_ms is an
// int[] of paragraph anchors. set adds one position to the array; clear
// removes one. Undo just toggles membership of at_ms — no separate
// "previous state" needs to be captured because the operation is its own
// inverse.
export interface BookmarkSetPayload {
  at_ms: number;
  [k: string]: unknown;
}

export interface BookmarkClearPayload {
  at_ms: number;
  [k: string]: unknown;
}

// Pure helper: compute the next bookmarks_at_ms array given the current
// array and the position being toggled. Adding inserts in sorted order;
// removing filters the value out. Used by toggleBookmark in MeetingReview
// and unit-tested independently.
export function toggleBookmarkArray(
  current: number[],
  atMs: number,
): { next: number[]; action: "set" | "clear" } {
  if (current.includes(atMs)) {
    return { next: current.filter((ms) => ms !== atMs), action: "clear" };
  }
  return { next: [...current, atMs].sort((a, b) => a - b), action: "set" };
}

// Full row snapshots captured before removal so reversal can restore them.
// Each interface mirrors the DB / in-memory shape so the undo path can
// re-insert without re-querying.
export interface ParagraphRevertPayload {
  turn_start_ms: number;
  turn_end_ms: number;
  // Full Correction row snapshots (removed from the DB via status=dismissed).
  removed_corrections: import("./corrections").Correction[];
  // Full SpanAttribution row snapshots (removed from transcripts.span_attributions).
  removed_span_attributions: import("./corrections").SpanAttribution[];
  // Full ParagraphSplit row snapshots (removed from transcripts.paragraph_splits).
  removed_paragraph_splits: import("./paragraphSplits").ParagraphSplit[];
  // Bookmark positions removed from meetings.bookmarks_at_ms (migration 0022).
  // Empty array when no bookmark fell on this paragraph. Multi-element when
  // more than one bookmarked position overlapped the turn's range.
  removed_bookmarks_at_ms: number[];
  [k: string]: unknown;
}

export interface MeetingEventPayload {
  // Open jsonb shape -- per event_type. See
  // supabase/migrations/0014_meeting_events.sql comments for the
  // current shapes (title_change, speaker_label_override,
  // resubmit_initiated, etc.).
  [k: string]: unknown;
}

export interface MeetingEvent {
  id: string;
  meeting_id: string;
  workspace_id: string;
  event_type: string;
  actor_type: "user" | "system";
  actor_id: string | null;
  payload: MeetingEventPayload | null;
  cost_usd: number | null;
  created_at: string;
}

// Fetch multiple meeting_events rows by their IDs. Used by the
// edit_session undo path to re-hydrate the sub-events.
export async function loadEventsByIds(
  eventIds: string[],
): Promise<MeetingEvent[]> {
  if (eventIds.length === 0) return [];
  const { data, error } = await supabase
    .from("meeting_events")
    .select("*")
    .in("id", eventIds)
    .order("created_at");
  if (error) throw error;
  return (data ?? []) as MeetingEvent[];
}

// Fetch all user-action meeting_events created at or after entryTimestamp
// for a given meeting. Used by Save (to gather sub_event_ids) and Cancel
// (to gather events to reverse).
export async function loadSessionEvents(
  meetingId: string,
  entryTimestamp: string,
): Promise<MeetingEvent[]> {
  const { data, error } = await supabase
    .from("meeting_events")
    .select("*")
    .eq("meeting_id", meetingId)
    .eq("actor_type", "user")
    .gte("created_at", entryTimestamp)
    .order("created_at");
  if (error) throw error;
  return (data ?? []) as MeetingEvent[];
}

// Fetch the most recent undoable user event for this meeting.
// Excludes undo_action events so pressing Undo twice undoes two distinct
// actions rather than undoing the undo.
export async function loadLastUserEvent(
  meetingId: string,
): Promise<MeetingEvent | null> {
  const { data, error } = await supabase
    .from("meeting_events")
    .select("*")
    .eq("meeting_id", meetingId)
    .eq("actor_type", "user")
    .neq("event_type", "undo_action")
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return (data as MeetingEvent) ?? null;
}

/** True when a meeting_events row of one of `eventTypes` exists after
 *  `sinceIso`. Used by the client-side in-flight indicators (rework /
 *  regenerate) to detect job completion without new plumbing. */
export async function hasMeetingEventSince(
  meetingId: string,
  eventTypes: string[],
  sinceIso: string,
): Promise<boolean> {
  const { data, error } = await supabase
    .from("meeting_events")
    .select("id")
    .eq("meeting_id", meetingId)
    .in("event_type", eventTypes)
    .gt("created_at", sinceIso)
    .limit(1);
  if (error) throw error;
  return (data ?? []).length > 0;
}

// Write one user event. Throws on Supabase error.
export async function logUserEvent(args: {
  meetingId: string;
  workspaceId: string;
  eventType: string;
  payload?: MeetingEventPayload | null;
}): Promise<void> {
  const { data: userRes, error: authErr } = await supabase.auth.getUser();
  if (authErr) throw authErr;
  const userId = userRes.user?.id;
  if (!userId) {
    // Defensive: the SPA shouldn't reach a writer while signed out, but
    // surface clearly rather than letting RLS fail opaquely with
    // actor_id = null. (Policy requires actor_id = auth.uid().)
    throw new Error("not signed in -- cannot write meeting_events row");
  }

  const { error } = await supabase.from("meeting_events").insert({
    meeting_id: args.meetingId,
    workspace_id: args.workspaceId,
    event_type: args.eventType,
    actor_type: "user",
    actor_id: userId,
    payload: args.payload ?? null,
  });
  if (error) throw error;
}
